import axios from "axios";
import { buildApiUrl } from "../config/api";

export interface TimeEntry {
  id: string;
  userId: string;
  projectId?: string;
  description: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  tags?: string[];
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
  createdAt: string;
  updatedAt: string;
}

export interface CreateTimeEntryInput {
  description: string;
  projectId?: string;
  startTime?: string;
  tags?: string[];
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
}

export interface UpdateTimeEntryInput {
  description?: string;
  startTime?: string;
  projectId?: string;
  endTime?: string;
  tags?: string[];
  location?: string;
}

export interface TokenBasedResponse {
  timeEntries: TimeEntry[];
  count: number;
  totalCount: number;
  nextToken?: string;
  hasNextPage: boolean;
}

export interface RunningEntryResponse {
  runningEntry: TimeEntry | null;
  isRunning: boolean;
}

export interface TimeTrackingReportResponse {
  timeEntries: TimeEntry[];
  count: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  filters: {
    projectId: string | null;
    location: string | null;
  };
}

class TimeTrackingService {
  private baseUrl = buildApiUrl("time-entries");

  /**
   * Get time entries with token-based pagination
   * This is the new efficient method for loading all entries
   */
  async getTimeEntriesWithToken(
    limit: number = 1000,
    nextToken?: string,
    adminUserId?: string,
    activeProjectsOnly?: boolean
  ): Promise<TokenBasedResponse> {
    const params: Record<string, string | number> = {
      limit,
    };

    if (nextToken) params.nextToken = nextToken;
    if (adminUserId) params.adminUserId = adminUserId;
    if (activeProjectsOnly) params.activeProjectsOnly = "true";

    const response = await axios.get(this.baseUrl, { params });
    return response.data;
  }

  /**
   * Get all entries for a specific project (used to reload cache after project reactivation)
   */
  async getEntriesByProjectId(projectId: string): Promise<TokenBasedResponse> {
    const response = await axios.get(this.baseUrl, {
      params: { projectId },
    });
    return response.data;
  }

  /**
   * Get time entries for admin/reader with user filtering
   * Used by Entry Management page to view entries from all users or specific user
   */
  async getTimeEntriesForAdmin(
    selectedUserId: string, // "all" or specific user ID
    limit: number = 50,
    nextToken?: string
  ): Promise<TokenBasedResponse> {
    return this.getTimeEntriesWithToken(limit, nextToken, selectedUserId);
  }

  /**
   * Get currently running time entry
   */
  async getRunningEntry(): Promise<RunningEntryResponse> {
    const response = await axios.get(`${this.baseUrl}/running`);
    return response.data;
  }

  /**
   * Start a new time entry
   */
  async startTimeEntry(
    input: CreateTimeEntryInput
  ): Promise<{ timeEntry: TimeEntry; message: string }> {
    const response = await axios.post(this.baseUrl, input);
    // Dispatch event for logo animation
    window.dispatchEvent(new CustomEvent("timeEntryStarted"));
    return response.data;
  }

  /**
   * Create a completed time entry (manual entry)
   */
  async createTimeEntry(
    input: CreateTimeEntryInput & { endTime: string }
  ): Promise<{ timeEntry: TimeEntry; message: string }> {
    const response = await axios.post(this.baseUrl, input);
    return response.data;
  }

  /**
   * Stop a running time entry
   */
  async stopTimeEntry(
    id: string,
    adminUserId?: string
  ): Promise<{ timeEntry: TimeEntry; message: string }> {
    const params: Record<string, string> = {};
    if (adminUserId) {
      params.adminUserId = adminUserId;
    }

    const response = await axios.post(
      `${this.baseUrl}/${id}/stop`,
      {},
      { params }
    );
    // Dispatch event for logo animation
    window.dispatchEvent(new CustomEvent("timeEntryStopped"));
    return response.data;
  }

  /**
   * Update a time entry
   */
  async updateTimeEntry(
    id: string,
    updates: UpdateTimeEntryInput,
    adminUserId?: string
  ): Promise<{ timeEntry: TimeEntry; message: string }> {
    const params: Record<string, string> = {};
    if (adminUserId) {
      params.adminUserId = adminUserId;
    }

    const response = await axios.put(`${this.baseUrl}/${id}`, updates, {
      params,
    });
    // Dispatch event for logo animation (only if it affects running status)
    if (updates.endTime !== undefined) {
      window.dispatchEvent(new CustomEvent("timeEntryUpdated"));
    }
    return response.data;
  }

  /**
   * Delete a time entry
   */
  async deleteTimeEntry(id: string): Promise<{ message: string }> {
    const response = await axios.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get a specific time entry
   */
  async getTimeEntry(id: string): Promise<{ timeEntry: TimeEntry }> {
    const response = await axios.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get time entries for reporting (date range filter, no pagination)
   */
  async getTimeEntriesForReport(
    startDate: string,
    endDate: string,
    projectId?: string,
    location?: string,
    sort?: string
  ): Promise<TimeTrackingReportResponse> {
    const params: Record<string, string> = {
      startDate,
      endDate,
    };

    if (projectId) params.projectId = projectId;
    if (location) params.location = location;
    if (sort) params.sort = sort;

    const response = await axios.get(`${this.baseUrl}/report`, { params });

    return response.data;
  }

  /**
   * Format duration in seconds to human readable format
   */
  formatDuration(seconds: number): string {
    if (seconds < 0) seconds = 0;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  /**
   * Calculate duration between two dates
   */
  calculateDuration(startTime: string, endTime?: string): number {
    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    return Math.floor((end - start) / 1000);
  }
}

export const timeTrackingService = new TimeTrackingService();
